
import { debounce } from "lodash";
import { Options, Vue } from "vue-class-component";
import { Data } from "@vue/composition-api";
import ScanFailedCard from "@/app/ui/views/out-going-shipment/sti/component/scan-failed-card/index.vue";
import {
  HubManifestTemporaryItem,
  InHubScanFailed,
  InHubScanGenerate,
  InHubScanGenerateList,
  InHubScanTemporaryListRequest
} from "@/domain/entities/HubToHub";
import { Pagination } from "@/domain/entities/Pagination";
import {
  getDataLocalStorage,
  storeDatatoLocalStorage,
  formatByCountry
} from "@/app/infrastructures/misc/Utils";
import { GenerateInHubScanRequest } from "@/data/payload/api/HubToHubApiRequest";
import { playNotification } from "@/app/infrastructures/misc/UtilsAudio";
import { HubToHubController } from "@/app/ui/controllers/HubToHubController";
import ModalManifest from "../../components/modal-manifest.vue";

@Options({
  components: {
    ScanFailedCard,
    ModalManifest
  }
})
export default class UpdateInHub extends Vue {
  openModal = false;
  mounted(): void {
    this.fetchTemporaryList();
    this.controller.setScanFailedData([]);
    const inHubScanFailedList = JSON.parse(
      getDataLocalStorage("in-hub-scan-failed")
    ) as InHubScanFailed[];

    this.controller.setScanFailedData(inHubScanFailedList || []);
  }
  form = {
    sttNumber: "",
    search: ""
  };
  illustrationData = {
    flag: "",
    isSTT: true,
    regionName: "",
    isStiDest: false,
    isInvalid: false,
    isPartially: false,
    isEmpty: true,
    sttNo: "",
    isUnderpaid: false,
    type: "STT" as "STT" | "BAG",
    isDangerousGoods: false,
    isRelabel: false
  };
  refs?: Data = undefined;
  loadingIllustration = false;
  tempSttNo = "";

  controller = HubToHubController;

  goBack() {
    this.$router.back();
  }

  clearLocalStorage() {
    this.controller.setScanFailedData([]);
    storeDatatoLocalStorage("in-hub-scan-failed", []);
  }

  onScanSttNumber = debounce((val: string) => {
    const splitVal = val.split("#")[0];

    this.form.sttNumber = splitVal;
    this.generateInHubScan(splitVal);
    this.setSttInputEl();
  }, 500);

  generateInHubScan(sttNo: string) {
    if (this.loadingIllustration) return;
    if (!this.form.sttNumber) return;
    const payload = new GenerateInHubScanRequest({
      scanNo: sttNo
    });
    this.tempSttNo = sttNo;

    this.loadingIllustration = true;
    this.controller
      .generateInHubScan(payload)
      .then(async (resp) => {
        if (resp.success) {
          this._processSuccessScan(resp);
        } else {
          playNotification("error");
          this.illustrationData = {
            ...this.illustrationData,
            isInvalid: true,
            isEmpty: false,
            isPartially: false
          };
          this.form.sttNumber = "";
        }
      })
      .catch((err) => {
        this._processErrorScan(err);
      })
      .finally(() => {
        this.loadingIllustration = false;
      });
  }

  async _processSuccessScan(resp: InHubScanGenerateList) {
    this._resetForm();

    const isFailed = resp.data[0]?.hmtDemOrBagStatus === "failed";
    const isPartially = resp.data[0]?.hmtDemOrBagStatus === "partially";

    // 🔹 Handle Failed / Partially
    if (isFailed) {
      this._handleFailedScan();
      return;
    }

    if (isPartially) {
      this._handlePartiallyScan(resp);
    } else {
      this._handleSuccessScan(resp);
    }
  }

  private _resetForm() {
    this.form = {
      ...this.form,
      search: "",
      sttNumber: ""
    };
  }

  private _handleFailedScan() {
    const scanFailedData = this.controller.scanFailedData || [];
    const failedItem = {
      sttOrBag: this.tempSttNo,
      errorMessage: "STT tidak valid"
    };
    const isExist = scanFailedData.some(
      (existing) => existing.sttOrBag === this.tempSttNo
    );
    if (!isExist) {
      scanFailedData.unshift(failedItem);
    }
    this.controller.setScanFailedData(scanFailedData);
    storeDatatoLocalStorage("in-hub-scan-failed", scanFailedData);

    this.illustrationData = {
      ...this.illustrationData,
      isInvalid: true,
      isEmpty: false,
      isPartially: false
    };

    playNotification("error");
  }

  private _handlePartiallyScan(resp: InHubScanGenerateList) {
    const scanFailedData = this._mergeFailedData(resp);
    this.controller.setScanFailedData(scanFailedData);
    storeDatatoLocalStorage("in-hub-scan-failed", scanFailedData);

    this.illustrationData = {
      ...this.illustrationData,
      isInvalid: false,
      isEmpty: false,
      isPartially: true
    };

    // hanya simpan yang success ke temporary list
    const successData = resp.data.filter(
      (item) => item.hmtScanStatus === "success"
    );
    this._updateTemporaryList(successData);

    playNotification("success");
  }

  private _handleSuccessScan(resp: InHubScanGenerateList) {
    this.illustrationData = {
      ...this.illustrationData,
      isInvalid: false,
      isEmpty: false,
      isPartially: false
    };

    const newData = resp.data.filter(
      (item) => !this.checkIsSTTAlreadyExist(item.hmtSttNo)
    );

    if (newData.length > 0) {
      this._updateTemporaryList(newData);
    }

    playNotification("success");
  }

  private _mergeFailedData(resp: InHubScanGenerateList) {
    const failedData = resp.data.filter(
      (item) => item.hmtScanStatus !== "success"
    );
    const scanFailedData = this.controller.scanFailedData || [];

    const failedDataMapped = failedData.map((item) => ({
      sttOrBag: item.hmtSttNo,
      errorMessage: "STT tidak valid"
    }));

    failedDataMapped.forEach((failedItem) => {
      const isExist = scanFailedData.some(
        (existing) => existing.sttOrBag === failedItem.sttOrBag
      );
      if (!isExist) {
        scanFailedData.unshift(failedItem);
      }
    });

    return scanFailedData;
  }

  private _updateTemporaryList(data: any[]) {
    const currentTemporaryListData = this.temporaryList;
    const newTemporaryDataList = data.map((item) =>
      this._mapInHubScanTemporary(item)
    );

    newTemporaryDataList.forEach((item) => {
      currentTemporaryListData.unshift(item);
    });

    this.controller.setInHubScanTemporaryData(currentTemporaryListData);
  }

  _processErrorScan(err: any) {
    let isUnderpaid = false;
    if (err?.response) {
      isUnderpaid = err.response.data.message.en.includes("underpaid");
    }
    playNotification("error");
    this.illustrationData = {
      ...this.illustrationData,
      isInvalid: true,
      isEmpty: false,
      isSTT: true,
      isUnderpaid
    };
    this.form.sttNumber = "";
  }

  _mapInHubScanTemporary(item: InHubScanGenerate): HubManifestTemporaryItem {
    if (!item) return new HubManifestTemporaryItem();
    return new HubManifestTemporaryItem({
      hmtId: item.hmtId,
      hmtSttNo: item.hmtSttNo,
      hmtBagNo: item.hmtBagNo,
      hmtRefNo: item.hmtRefNo,
      hmtPieces: item.hmtPiece,
      hmtGrossWeight: item.hmtGrossWeight,
      hmtProductType: item.hmtProductType,
      hmtDemNo: item.hmtDemNo
    });
  }

  checkIsSTTAlreadyExist(sttNo: string): boolean {
    if (this.isSttNumber(sttNo)) {
      return this.temporaryList.some((item) => item.hmtSttNo === sttNo);
    }

    return false;
  }

  isSttNumber(sttNumber: string): boolean {
    return /^\d{1,2}LP\w+$/g.test(sttNumber);
  }

  onSetFocusRefs() {
    this.refs = this.$refs;
  }

  setSttInputEl() {
    const inputElement = this.$refs?.["inputStt"] as
      | HTMLInputElement
      | undefined;

    if (inputElement) {
      inputElement.focus();
    }
  }

  onSearch(val: string): void {
    this.form.search = val;
    this.fetchTemporaryList();
  }

  onClearSearch(): void {
    this.form.search = "";
    this.fetchTemporaryList();
  }

  onConfirmStiManifest() {
    // action proses manifest
    this.openModal = true;
    // clear local storage failed stt when success process
    this.clearLocalStorage();
  }

  get columns() {
    return [...this.columnsFirstSection, ...this.columnSecondSection];
  }

  get columnsFirstSection() {
    return [
      {
        name: this.$t("hubToHub.no"),
        styleHead: "text-left w-12",
        render: (item: HubManifestTemporaryItem, index: number) => {
          return `<div class="text-sm text-black-lp-300 text-left">${index +
            1 +
            this.pagination.limit * (this.pagination.page - 1)}</div>`;
        }
      },
      {
        name: this.$t("hubToHub.noBagDem"),
        styleHead: "text-left w-40",
        render: (item: HubManifestTemporaryItem) =>
          `<div class="text-sm text-black-lp-300 text-left">
            ${item.hmtBagNo || item.hmtDemNo || "-"}
          </div>`
      },
      {
        name: this.$t("hubToHub.noStt"),
        styleHead: "text-left w-40",
        render: (item: HubManifestTemporaryItem) =>
          `<div class="text-sm text-black-lp-300 text-left">
            ${item.hmtSttNo || "-"}
          </div>`
      }
    ];
  }

  get columnSecondSection() {
    return [
      {
        name: this.$t("hubToHub.refNo"),
        styleHead: "text-left w-40",
        render: (item: HubManifestTemporaryItem) =>
          `<div class="text-sm text-black-lp-300 text-left break-all">
            ${item.hmtRefNo || "-"}
          </div>`
      },
      {
        name: this.$t("hubToHub.koli"),
        styleHead: "text-left w-36",
        render: (item: HubManifestTemporaryItem) =>
          `<div class="text-sm text-black-lp-300 text-left" data-testid="hub-to-hub-update-cell-koli">
            ${formatByCountry(item.hmtPieces, { type: "quantity" })}
          </div>`
      },
      {
        name: this.$t("hubToHub.weight"),
        styleHead: "text-left w-36",
        render: (item: HubManifestTemporaryItem) =>
          `<div class="text-sm text-black-lp-300 text-left" data-testid="hub-to-hub-update-cell-berat">
            ${formatByCountry(item.hmtGrossWeight, {
              type: "weight",
              suffix: " Kg"
            })}
          </div>`
      },
      {
        name: this.$t("hubToHub.product"),
        styleHead: "text-left w-36",
        render: (item: HubManifestTemporaryItem) =>
          `<div class="text-sm text-black-lp-300 text-left">
            <div class="rounded px-2 py-0 bg-gray-lp-400">
            ${item.hmtProductType}
            </div>
          </div>`
      }
    ];
  }

  get totalStt() {
    return this.temporaryList.length;
  }

  get calculatedStt() {
    return this.temporaryList.reduce(
      (a, b) => {
        return {
          sttTotalPiece: a.sttTotalPiece + b.hmtPieces,
          sttGrossWeight: a.sttGrossWeight + b.hmtGrossWeight
        };
      },
      {
        sttTotalPiece: 0,
        sttGrossWeight: 0
      }
    );
  }

  get formattedTotalStt(): string {
    return formatByCountry(this.totalStt, { type: "quantity" });
  }

  get formattedTotalPieces(): string {
    return formatByCountry(this.totalPieces, { type: "quantity" });
  }

  get formattedGrossWeight(): string {
    return formatByCountry(this.totalGrossWeight, {
      type: "weight",
      suffix: " kg"
    });
  }

  async fetchTemporaryList(): Promise<void> {
    const payload = new InHubScanTemporaryListRequest({
      q: this.form.search
    });

    const res = await this.controller.getListHubManifestTemporary(payload);

    if (!res.data.length) {
      this.controller.setInHubScanTemporaryData([]);
      this.controller.setInHubScanPagination(new Pagination(1, 20, 0));
      this.resetIllustration();
      return;
    }
    if (this.form.search.length) return;
  }

  resetIllustration() {
    this.illustrationData = {
      flag: "",
      isSTT: true,
      regionName: "",
      isStiDest: false,
      isInvalid: false,
      isPartially: false,
      isEmpty: true,
      sttNo: "",
      isUnderpaid: false,
      type: "STT",
      isDangerousGoods: false,
      isRelabel: false
    };
  }

  get totalPieces() {
    return this.calculatedStt.sttTotalPiece;
  }
  get totalGrossWeight() {
    return this.calculatedStt.sttGrossWeight;
  }

  get temporaryList() {
    return this.controller.InHubScanTemporaryList;
  }
  get pagination() {
    return this.controller.inHubScanPagination;
  }
  get InHubScanFailedList() {
    return this.controller.scanFailedData;
  }
}
